import { useState, useEffect } from 'react';
import axios from '../../api/axios';
import './EventPage.css';
import {
  TextField,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import axiosInstance from '../../api/axios';
const API_BASE_URL = axiosInstance.defaults.baseURL;

// Build a local Date from date (YYYY-MM-DD or ISO) + time
function toLocalDate(dateInput, timeStr) {
  if (!dateInput) return null;

  // get Y-M-D without UTC shifts
  let y, m, d;
  if (dateInput instanceof Date) {
    y = dateInput.getFullYear();
    m = dateInput.getMonth() + 1;
    d = dateInput.getDate();
  } else {
    const iso = String(dateInput).split('T')[0]; // "YYYY-MM-DD"
    [y, m, d] = iso.split('-').map(Number);
  }

  // normalize time to HH:MM (accept H:MM, HH:MM, HH:MM:SS)
  let hh = 0,
    mm = 0;
  if (typeof timeStr === 'string') {
    const t = timeStr.trim();
    const m2 = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (m2) {
      hh = parseInt(m2[1], 10);
      mm = parseInt(m2[2], 10);
    }
  }

  return new Date(y, m - 1, d, hh, mm, 0, 0); // local time
}

function formatEventDateRange(event) {
  // Fallbacks for older records
  const start = toLocalDate(
    event.date?.split('T')[0] || event.date,
    event.startTime,
  );
  const end = event.endTime
    ? toLocalDate(event.date?.split('T')[0] || event.date, event.endTime)
    : null;

  const datePart = (
    start || toLocalDate(event.date?.split('T')[0] || event.date, '00:00')
  ).toLocaleString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const timePart = start
    ? start.toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    : '';

  const endPart = end
    ? end.toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    : '';

  if (timePart && endPart) return `${datePart} • ${timePart} – ${endPart}`;
  if (timePart) return `${datePart} • ${timePart}`;
  return datePart; // legacy events with no time
}

function EventPage() {
  // WebSocket connection
  useEffect(() => {
    const timeout = setTimeout(() => {
      const socket = new WebSocket(`ws://${window.location.hostname}:5001/ws`);

      socket.onopen = () => {
        // console.log('✅ WebSocket connected');
        socket.send('Hello server');
      };

      // socket.onmessage = (event) => {
      //   console.log('📩 Message from server:', event.data);
      // };

      socket.onclose = () => {
        console.log('❌ WebSocket disconnected');
      };
    }, 200); // delay connection

    return () => clearTimeout(timeout);
  }, []);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [events, setEvents] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [sortOption, setSortOption] = useState('recent');
  const [filterType, setFilterType] = useState('all');
  const [visibleEvents, setVisibleEvents] = useState(8);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [stats, setStats] = useState(null);
  const [eventIdToDelete, setEventIdToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventData, setEventData] = useState({
    title: '',
    date: '',
    startTime: '',
    endTime: '',
    location: '',
    description: '',
    image: null,
    draft: false,
    imagePreview: null,
  });

  const fetchStats = async () => {
    try {
      const res = await axios.get('/api/events/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching event stats:', error);
    }
  };

  const handleViewEvent = async (eventId) => {
    try {
      await axios.put(`/api/events/${eventId}/view`);
      // redirect to or open full event view if needed
    } catch (error) {
      console.error('Error tracking event view:', error);
    }
  };

  const filteredEvents = [...events]
    .filter((event) => event.title.toLowerCase().includes(search.toLowerCase()))
    .filter((event) => {
      // const eventDate = new Date(event.date);
      const baseDateStr = event.date?.split('T')[0] || event.date;
      const end = event.endTime
        ? toLocalDate(baseDateStr, event.endTime)
        : toLocalDate(baseDateStr, event.startTime || '00:00');
      const now = new Date();

      if (filterType === 'published') return !event.draft;
      if (filterType === 'draft') return event.draft;
      if (filterType === 'archived') return end && end < now;
      return true;
    })

    .sort((a, b) => {
      if (sortOption === 'recentlyAdded') {
        return new Date(b.createdAt) - new Date(a.createdAt); // newest first
      }

      if (sortOption === 'earliestAdded') {
        return new Date(a.createdAt) - new Date(b.createdAt); // oldest first
      }

      if (sortOption === 'nearest') {
        const today = new Date();
        const baseA = a.date?.split('T')[0] || a.date;
        const baseB = b.date?.split('T')[0] || b.date;
        const aStart = toLocalDate(baseA, a.startTime || '00:00');
        const bStart = toLocalDate(baseB, b.startTime || '00:00');
        const aDiff = Math.abs(aStart - today);
        const bDiff = Math.abs(bStart - today);
        return aDiff - bDiff;
      }

      if (sortOption === 'title') {
        return a.title.localeCompare(b.title);
      }

      return 0;
    });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get('/api/events');
      const sortedEvents = response.data.sort(
        (a, b) => new Date(b.date) - new Date(a.date),
      );
      setEvents(sortedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleChange = (e) => {
    setEventData({ ...eventData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEventData({
        ...eventData,
        image: file,
        imagePreview: URL.createObjectURL(file),
      });
    }
  };

  const handleEditClick = (event) => {
    setEventData({
      ...event,
      image: null,
      imagePreview: event.image?.filePath
        ? `${API_BASE_URL}/${event.image.filePath}`
        : null,
      date: event.date ? new Date(event.date).toISOString().split('T')[0] : '',
      startTime: event.startTime || '',
      endTime: event.endTime || '',
    });
    setSelectedEventId(event._id);
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/api/events/${eventIdToDelete}`);
      setDeleteDialogOpen(false);
      setEventIdToDelete(null);
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const handleSaveEvent = async () => {
    if (eventData.startTime && eventData.endTime) {
      const s = toLocalDate(eventData.date, eventData.startTime);
      const e = toLocalDate(eventData.date, eventData.endTime);
      if (s && e && e < s) {
        alert('End time must be after start time.');
        return;
      }
    }

    const formData = new FormData();
    formData.append('title', eventData.title);
    formData.append('date', eventData.date);
    formData.append('startTime', eventData.startTime || '');
    formData.append('endTime', eventData.endTime || '');
    formData.append('location', eventData.location);
    formData.append('description', eventData.description);
    formData.append('draft', eventData.draft ? 'true' : 'false');
    if (eventData.image) formData.append('image', eventData.image);

    try {
      if (editMode) {
        await axios.put(`/api/events/${selectedEventId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axios.post('/api/events', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setOpen(false);
      setEditMode(false);
      setEventData({
        title: '',
        date: '',
        startTime: '',
        endTime: '',
        location: '',
        description: '',
        image: null,
        draft: false,
        imagePreview: null,
      });
      fetchEvents();
      console.log('Event saved successfully', eventData);
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchStats();
  }, []);

  const handleExportCSV = () => {
    const rows = filteredEvents.map((event) => ({
      Title: event.title,
      DateTime: formatEventDateRange(event),
      Location: event.location,
      Description: event.description,
      Draft: event.draft ? 'Yes' : 'No',
      Views: event.views || 0,
    }));

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [
        Object.keys(rows[0]).join(','),
        ...rows.map((e) =>
          Object.values(e)
            .map((v) => `"${v}"`)
            .join(','),
        ),
      ].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'events_export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="container">
      <div className="main-content">
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '5em',
          }}
        >
          {stats && (
            <div className="event-stats-row">
              <div>
                <strong>Total:</strong> {stats.total}
              </div>
              <div>
                <strong>Published:</strong> {stats.published}
              </div>
              <div>
                <strong>Draft:</strong> {stats.draft}
              </div>
              <div>
                <strong>Upcoming:</strong> {stats.upcoming}
              </div>
              <div>
                <strong>Past:</strong> {stats.past}
              </div>
              <div>
                <strong>Top:</strong> {stats.topViewed?.[0]?.title || 'N/A'}
              </div>
            </div>
          )}
        </div>
        <div className="search-add-container">
          <TextField
            variant="outlined"
            placeholder="Search events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-field"
            size="small"
          />

          <select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            className="event-sort-select"
          >
            <option value="recentlyAdded">Recently Added</option>
            <option value="lastAdded">Last Added</option>
            <option value="nearest">Nearest to Today</option>
            <option value="title">Title A–Z</option>
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="event-filter-select"
          >
            <option value="all">All</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>

          <Button
            variant="contained"
            className="add-event-button"
            onClick={() => {
              setEventData({
                title: '',
                date: '',
                startTime: '',
                endTime: '',
                location: '',
                description: '',
                image: '',
                // image: null,
              });
              setEditMode(false);
              setSelectedEventId(null);
              setOpen(true);
            }}
          >
            Add Event
          </Button>
          <Button
            variant="contained"
            className="export-csv-button"
            onClick={handleExportCSV}
          >
            Export CSV
          </Button>
        </div>

        <div container spacing={3} className="event-list">
          <div className="event-grid">
            {filteredEvents.slice(0, visibleEvents).map((event) => (
              <div
                className="event-card"
                key={event._id}
                onClick={() => handleViewEvent(event._id)}
                style={{ cursor: 'pointer' }}
              >
                {event.draft && (
                  <span className="admin-draft-badge">Draft</span>
                )}
                {(() => {
                  const base = event.date?.split('T')[0] || event.date;
                  const end = event.endTime
                    ? toLocalDate(base, event.endTime)
                    : toLocalDate(base, event.startTime || '00:00');
                  return end && end < new Date();
                })() && <span className="archived-badge">Archived</span>}

                <img
                  className="event-image"
                  src={
                    event.image?.filePath
                      ? `${API_BASE_URL}/${event.image.filePath}`
                      : '/logo.png'
                  }
                  alt={event.title}
                />
                <div className="event-card-content">
                  <h3 className="event-title">{event.title}</h3>
                  <p className="event-date">
                    📅 {formatEventDateRange(event)}
                    <br /> 📍 {event.location}
                  </p>

                  <p className="event-description">{event.description}</p>

                  <div className="event-footer">
                    <p>
                      <strong>Views:</strong> {event.views || 0}
                    </p>

                    <div className="event-actions">
                      <button
                        className="edit-btn"
                        onClick={() => handleEditClick(event)}
                      >
                        Edit
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => {
                          setEventIdToDelete(event._id);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {visibleEvents < filteredEvents.length && (
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                className="load-more-btn"
                onClick={() => setVisibleEvents((prev) => prev + 6)}
              >
                Load More
              </button>
            </div>
          )}

          {filteredEvents.length === 0 && (
            <p
              style={{ textAlign: 'center', marginTop: '2rem', color: '#888' }}
            >
              No events found. Try adjusting your search or filters.
            </p>
          )}
        </div>
      </div>

      {/* Dialog for adding a new event */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle className="dialog-title">Add New Event</DialogTitle>
        <DialogContent className="dialog-content">
          <TextField
            name="title"
            fullWidth
            margin="dense"
            label="Event Title"
            value={eventData.title}
            onChange={handleChange}
          />
          <TextField
            name="date"
            fullWidth
            margin="dense"
            label="Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={eventData.date}
            onChange={handleChange}
          />

          <Box display="flex" gap={2}>
            <TextField
              name="startTime"
              label="Start Time"
              type="time"
              value={eventData.startTime}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              margin="dense"
            />
            <TextField
              name="endTime"
              label="End Time"
              type="time"
              value={eventData.endTime}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              margin="dense"
            />
          </Box>
          <TextField
            name="location"
            fullWidth
            margin="dense"
            label="Location"
            value={eventData.location}
            onChange={handleChange}
          />
          <TextField
            name="description"
            fullWidth
            margin="dense"
            label="Description"
            multiline
            rows={3}
            value={eventData.description}
            onChange={handleChange}
          />
          <input
            type="file"
            className="file-input"
            accept="image/*"
            onChange={handleImageUpload}
          />
          {eventData.imagePreview && (
            <img
              src={eventData.imagePreview}
              alt="Preview"
              style={{
                width: '200px',
                height: '150px',
                objectFit: 'contain',
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <div className="draft-toggle">
            <input
              type="checkbox"
              name="draft"
              checked={eventData.draft || false}
              onChange={(e) =>
                setEventData({ ...eventData, draft: e.target.checked })
              }
            />
            <span>Save as Draft</span>
          </div>

          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSaveEvent}>
            {editMode ? 'Update Event' : 'Add Event'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Job Dialog Box */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle className="dialog-title">Delete Event</DialogTitle>
        <DialogContent className="dialog-content">
          Are you sure you want to delete this event?
        </DialogContent>
        <DialogActions className="dialog-actions">
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default EventPage;
